import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #defaultInfinite = undefined;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#mobileLoveStyleMediaQuery.addEventListener('change', this.#syncLoveStyleSlide, { signal });
    this.#mobileLoveStyleMediaQuery.addEventListener('change', this.#syncInfiniteMode, { signal });
    queueMicrotask(this.#syncLoveStyleSlide);
    queueMicrotask(this.#syncInfiniteMode);
  }

  #controller = new AbortController();
  #mobileLoveStyleMediaQuery = window.matchMedia('(max-width: 749px)');

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;
    if (this.#getMediaSignature() === this.#getMediaSignature(newMediaGallery)) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * @param {Element} [mediaGallery]
   * @returns {string}
   */
  #getMediaSignature(mediaGallery = this) {
    return Array.from(mediaGallery.querySelectorAll('.product-media[data-media-id]'))
      .map((media) => /** @type {HTMLElement} */ (media).dataset.mediaId)
      .filter(Boolean)
      .join(',');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #syncLoveStyleSlide = () => {
    const loveStyleSlide = this.querySelector('[data-love-style-slide]');
    if (!(loveStyleSlide instanceof HTMLElement)) return;

    const isQuickAddModalGallery = this.closest('#quick-add-modal-content') !== null;
    const showLoveStyleSlide = !isQuickAddModalGallery && this.#mobileLoveStyleMediaQuery.matches;
    loveStyleSlide.toggleAttribute('hidden', !showLoveStyleSlide);

    this.querySelectorAll('[data-love-style-control]').forEach((control) => {
      if (control instanceof HTMLElement) {
        const target = control.closest('li') ?? control;
        if (target instanceof HTMLElement) {
          target.hidden = !showLoveStyleSlide;
        }
      }
    });

    const mediaCount = Number(this.dataset.mediaCount || '0');
    const effectiveSlideCount = showLoveStyleSlide ? mediaCount + 1 : mediaCount;
    this.querySelectorAll('[data-slideshow-total-mobile]').forEach((totalElement) => {
      if (totalElement instanceof HTMLElement) {
        totalElement.textContent = String(effectiveSlideCount);
      }
    });
    this.querySelectorAll('[data-slideshow-total-desktop]').forEach((totalElement) => {
      if (totalElement instanceof HTMLElement) {
        totalElement.textContent = String(mediaCount);
      }
    });

    const slideshow = this.slideshow;
    if (!slideshow) return;

    const visibleSlides = slideshow.slides ?? [];
    const lastVisibleSlideIndex = Math.max(visibleSlides.length - 1, 0);

    if (!showLoveStyleSlide && slideshow.current > lastVisibleSlideIndex) {
      slideshow.select(lastVisibleSlideIndex, undefined, { animate: false });
    } else {
      slideshow.current = Math.min(slideshow.current, lastVisibleSlideIndex);
    }

    const controls = this.querySelectorAll('slideshow-controls');
    controls.forEach((control) => {
      if (control instanceof HTMLElement) {
        control.hidden = effectiveSlideCount <= 1;
      }
    });

    this.querySelectorAll('.slideshow-controls__arrows').forEach((arrows) => {
      if (arrows instanceof HTMLElement) {
        arrows.hidden = effectiveSlideCount <= 1;
      }
    });

    this.#updateScrollbar(slideshow);
  };

  #syncInfiniteMode = () => {
    const slideshow = this.slideshow;
    if (!slideshow) return;
    const isProductTemplateGallery = this.closest('body[data-template="product"]') !== null;
    const isQuickAddModalGallery = this.closest('#quick-add-modal-content') !== null;
    if (!isProductTemplateGallery && !isQuickAddModalGallery) return;

    if (this.#defaultInfinite == null) {
      this.#defaultInfinite = slideshow.hasAttribute('infinite');
    }

    if (this.#mobileLoveStyleMediaQuery.matches) {
      slideshow.removeAttribute('infinite');
    } else if (this.#defaultInfinite) {
      slideshow.setAttribute('infinite', '');
    } else {
      slideshow.removeAttribute('infinite');
    }

    // Re-run the current setter so boundary arrows reflect the active infinite mode.
    slideshow.current = slideshow.current;
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #updateScrollbar(slideshow) {
    const { scroller, scrollbar, scrollbarThumb } = slideshow.refs;
    if (!scroller || !scrollbar || !scrollbarThumb) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 0) {
      scrollbar.hidden = true;
      return;
    }

    scrollbar.hidden = false;
    const trackWidth = scrollbar.clientWidth;
    if (trackWidth <= 0) return;

    const thumbWidth = Math.min(Math.max((scroller.clientWidth / scroller.scrollWidth) * trackWidth, 24), trackWidth);
    const maxThumbTravel = Math.max(trackWidth - thumbWidth, 0);
    const ratio = maxScroll ? scroller.scrollLeft / maxScroll : 0;
    const translateX = maxThumbTravel * ratio;

    scrollbarThumb.style.width = `${thumbWidth}px`;
    scrollbarThumb.style.transform = `translateX(${translateX}px)`;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
